import { AxiosRequestConfig } from "axios";

const isAbsoluteUrl = (url: string): boolean => {
  return /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url);
};

/**
 * Resolve a url the way the browser will, so a relative API url (admin's
 * '/api/management') can be compared against an absolute request url.
 */
const absolute = (url: string): string => {
  try {
    return new URL(url, window.location.href).href;
  } catch (err) {
    // no window (SSR), or nothing to resolve against - compare as given
    return url;
  }
};

/**
 * Whether a url addresses our own API rather than one of the other hosts these
 * apps call. Matched on a path boundary, so '/api-docs' doesn't pass as '/api'.
 */
export const isApiUrl = (url: string, apiUrl: string): boolean => {
  if (!url || !apiUrl) {
    return false;
  }

  const base = absolute(apiUrl).replace(/\/+$/, "");
  const target = absolute(url);

  return (
    target === base ||
    target.startsWith(`${base}/`) ||
    target.startsWith(`${base}?`)
  );
};

/**
 * The url a request will actually be sent to. Mirrors how axios combines the
 * two - an absolute url wins over baseURL.
 */
const requestUrl = (config: AxiosRequestConfig): string => {
  const url = config.url || "";
  const baseURL = config.baseURL || "";

  return baseURL && !isAbsoluteUrl(url)
    ? `${baseURL.replace(/\/+$/, "")}/${url.replace(/^\/+/, "")}`
    : url;
};

/**
 * Request interceptor that attaches the user's current language
 * as an Accept-Language header so the backend can translate search queries.
 */
export const reqNoopInterceptor = (options?: any) => {
  return async (config: AxiosRequestConfig): Promise<any> => {
    const lang =
      typeof sessionStorage !== "undefined"
        ? sessionStorage.getItem("lang") || "en"
        : "en";

    config.headers = config.headers || {};
    config.headers["Accept-Language"] = lang;

    return config;
  };
};

/**
 * Request interceptor that attaches the access token - to our own API only.
 * The other hosts these apps call over the same axios instance (titiler, tile
 * CDNs) have no business seeing a token minted for the API, which is why this
 * is an allow-list here rather than an axios default header.
 */
export const reqAuthInterceptor = (config: {
  apiUrl: string;
  getAccessToken(): string;
}) => {
  return async (request: AxiosRequestConfig): Promise<any> => {
    const accessToken = config.getAccessToken();

    if (accessToken && isApiUrl(requestUrl(request), config.apiUrl)) {
      request.headers = request.headers || {};
      request.headers.Authorization = `Bearer ${accessToken}`;
    }

    return request;
  };
};
